/**
 * Admin session (BFF cookie). Loads `/admin/auth/session`, publishes the CSRF token to the API
 * client, tracks recent-auth freshness, and clears itself on any 401.
 */
import { api, hasAnyCapability, type Capability } from '@agency/shared';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ApiError, call, onUnauthorized, routeUrl, setCsrfToken } from './api.js';

export type SessionStatus = 'loading' | 'authenticated' | 'anonymous';

export interface SessionState {
  status: SessionStatus;
  session: api.AdminSessionResponse | null;
  /** Error from the last session load other than 401. */
  error: ApiError | null;
  /** True when recentAuthAt is within the 10-minute window (computed live). */
  recentAuthFresh: boolean;
  /** Remaining seconds in the recent-auth window, 0 when stale. */
  recentAuthRemainingSeconds: number;
  has: (capability: Capability | readonly Capability[]) => boolean;
  reload: () => Promise<void>;
  signOut: () => Promise<void>;
  /** Redirect to the server BFF login; returns to the given SPA path afterwards. */
  login: (returnTo?: string) => void;
  /** Re-authenticate to refresh recentAuthAt (same login flow). */
  reauthenticate: (returnTo?: string) => void;
}

const SessionContext = createContext<SessionState | null>(null);

function currentPath(): string {
  return `${window.location.pathname}${window.location.search}`;
}

export function loginUrl(returnTo: string): string {
  return routeUrl('admin.auth.login', { query: { returnTo } });
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<SessionStatus>('loading');
  const [session, setSession] = useState<api.AdminSessionResponse | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const skewRef = useRef(0);

  const reload = useCallback(async () => {
    try {
      const res = await call('admin.auth.session');
      skewRef.current = new Date(res.data.serverTime).getTime() - Date.now();
      setCsrfToken(res.data.csrfToken);
      setSession(res.data);
      setError(null);
      setStatus('authenticated');
    } catch (e) {
      const err = ApiError.from(e);
      setCsrfToken(null);
      setSession(null);
      setStatus('anonymous');
      setError(err.status === 401 || err.code === 'user_suspended' ? null : err);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(
    () =>
      onUnauthorized(() => {
        setCsrfToken(null);
        setSession(null);
        setStatus('anonymous');
      }),
    [],
  );

  // Recent-auth countdown ticks every 15 seconds; a session poll every 5 minutes keeps expiry honest.
  useEffect(() => {
    const tick = window.setInterval(() => setNow(Date.now()), 15_000);
    return () => window.clearInterval(tick);
  }, []);
  useEffect(() => {
    if (status !== 'authenticated') return;
    const poll = window.setInterval(() => void reload(), 5 * 60_000);
    return () => window.clearInterval(poll);
  }, [status, reload]);

  const recentAuthRemainingSeconds = useMemo(() => {
    if (!session) return 0;
    const serverNow = now + skewRef.current;
    const elapsed = serverNow - new Date(session.recentAuthAt).getTime();
    return Math.max(0, Math.floor((api.RECENT_AUTH_WINDOW_MS - elapsed) / 1000));
  }, [session, now]);

  const value = useMemo<SessionState>(
    () => ({
      status,
      session,
      error,
      recentAuthFresh: recentAuthRemainingSeconds > 0,
      recentAuthRemainingSeconds,
      has: (capability) => (session ? hasAnyCapability(session.capabilities, capability) : false),
      reload,
      signOut: async () => {
        try {
          await call('admin.auth.logout', { input: {} });
        } finally {
          setCsrfToken(null);
          setSession(null);
          setStatus('anonymous');
        }
      },
      login: (returnTo) => {
        window.location.assign(loginUrl(returnTo ?? currentPath()));
      },
      reauthenticate: (returnTo) => {
        window.location.assign(loginUrl(returnTo ?? currentPath()));
      },
    }),
    [status, session, error, recentAuthRemainingSeconds, reload],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionState {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used inside SessionProvider');
  return ctx;
}
